import { Dropdown } from 'bootstrap'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

// No native white/light export of the wordmark exists — reuse the color PNG
// and flatten it to white via the .desktop-dark/.toggle-dark CSS filter
// (theme.scss), since this is what dark mode uses for the header logo.
import desktopDark from '@/assets/brand-logos/edunet-logo-color.png'
import desktopLogo from '@/assets/brand-logos/edunet-logo-color.png'
// toggle-dark.png/toggle-logo.png were leftover blank placeholder assets
// from the Zynix template — the mobile header (<992px) shows only these
// two, so the header logo was rendering as empty space on mobile. Reuse
// the real wordmark assets instead; the theme's own CSS already scales
// them down to a compact height on narrow viewports.
const toggleDark = desktopDark
const toggleLogo = desktopLogo
import { useLogout } from '@/features/auth/hooks/useAuth'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import type { AuthUser } from '@/types/auth'

const USER_TYPE_LABELS: Record<AuthUser['userType'], string> = {
  student: 'Student',
  recruiter_user: 'Recruiter',
  recruiter_admin: 'Recruiter Admin',
  super_admin: 'Super Admin',
}

interface AppHeaderProps {
  user: AuthUser
  onToggleSidebar: () => void
  onToggleThemeMode: () => void
  isDarkMode: boolean
}

export function AppHeader({
  user,
  onToggleSidebar,
  onToggleThemeMode,
  isDarkMode,
}: AppHeaderProps) {
  const navigate = useNavigate()
  const logout = useLogout()
  const profileToggleRef = useRef<HTMLButtonElement>(null)
  const dropdownInstanceRef = useRef<Dropdown | null>(null)

  // Bootstrap's dropdown is never otherwise imported in this codebase (only
  // Modal/Offcanvas are, for Modal.tsx/Drawer.tsx). Relying on data-bs-toggle
  // alone (Bootstrap's document-level click-delegation "data API") didn't
  // open the menu here, so — same pattern as Modal.tsx/Drawer.tsx — this
  // drives it explicitly via the instance's own imperative .toggle(),
  // triggered by a plain onClick, rather than depending on that delegation.
  useEffect(() => {
    const el = profileToggleRef.current
    if (!el) return

    const instance = new Dropdown(el)
    dropdownInstanceRef.current = instance

    return () => {
      instance.dispose()
      dropdownInstanceRef.current = null
    }
  }, [])

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        void navigate('/login', { replace: true })
      },
    })
  }

  return (
    <header className="app-header sticky" id="header">
      <div className="main-header-container container-fluid">
        <div className="header-content-left">
          <div className="header-element">
            <div className="horizontal-logo">
              <a href="/" className="header-logo">
                <img src={desktopLogo} alt="logo" className="desktop-logo" />
                <img src={toggleLogo} alt="logo" className="toggle-logo" />
                <img src={desktopDark} alt="logo" className="desktop-dark" />
                <img src={toggleDark} alt="logo" className="toggle-dark" />
              </a>
            </div>
          </div>

          <div className="header-element">
            <button
              type="button"
              aria-label="Toggle sidebar"
              className="sidemenu-toggle header-link btn btn-link"
              onClick={onToggleSidebar}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="header-link-icon"
                width="32"
                height="32"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" />
              </svg>
            </button>
          </div>
        </div>

        <ul className="header-content-right">
          <li className="header-element header-theme-mode">
            <button
              type="button"
              aria-label="Toggle dark mode"
              className="header-link layout-setting btn btn-link"
              onClick={onToggleThemeMode}
            >
              {isDarkMode ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="header-link-icon"
                  viewBox="0 0 256 256"
                >
                  <rect width="256" height="256" fill="none" />
                  <path
                    d="M108.11,28.11A96.09,96.09,0,0,0,227.89,147.89,96,96,0,1,1,108.11,28.11Z"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="header-link-icon"
                  viewBox="0 0 256 256"
                >
                  <rect width="256" height="256" fill="none" />
                  <line
                    x1="128"
                    y1="40"
                    x2="128"
                    y2="32"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <circle
                    cx="128"
                    cy="128"
                    r="56"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="64"
                    y1="64"
                    x2="56"
                    y2="56"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="64"
                    y1="192"
                    x2="56"
                    y2="200"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="192"
                    y1="64"
                    x2="200"
                    y2="56"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="192"
                    y1="192"
                    x2="200"
                    y2="200"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="40"
                    y1="128"
                    x2="32"
                    y2="128"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="128"
                    y1="216"
                    x2="128"
                    y2="224"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                  <line
                    x1="216"
                    y1="128"
                    x2="224"
                    y2="128"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="16"
                  />
                </svg>
              )}
            </button>
          </li>

          <NotificationBell />

          <li className="header-element dropdown">
            <button
              ref={profileToggleRef}
              type="button"
              className="header-link dropdown-toggle btn btn-link"
              id="mainHeaderProfile"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
              onClick={() => dropdownInstanceRef.current?.toggle()}
            >
              <div className="d-flex align-items-center">
                <span className="avatar avatar-sm avatar-rounded bg-primary-transparent me-xl-2 me-0">
                  {user.name.charAt(0).toUpperCase()}
                </span>
                <div className="d-xl-block d-none lh-1">
                  <span className="fw-medium lh-1">{user.name}</span>
                </div>
              </div>
            </button>
            <ul
              className="main-header-dropdown dropdown-menu pt-0 overflow-hidden header-profile-dropdown dropdown-menu-end"
              aria-labelledby="mainHeaderProfile"
            >
              <li>
                <div className="py-2 px-3 text-center">
                  <span className="fw-semibold">{user.name}</span>
                  <span className="d-block fs-12 text-muted">
                    {USER_TYPE_LABELS[user.userType]}
                  </span>
                </div>
              </li>
              <li className="py-2 px-3">
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100 waves-effect waves-light"
                  onClick={handleLogout}
                  disabled={logout.isPending}
                >
                  {logout.isPending ? 'Logging out…' : 'Log Out'}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </header>
  )
}
